import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckSquare, Square, ArrowRight, Zap, Wallet, DollarSign, Undo2 } from "lucide-react";

import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";

/** Iter 66 — Pay Bills (batch vendor payment). Mirror of ReceivePayment. */
export default function PayBills() {
  const [currency, setCurrency] = useState("USD");
  const [vendors, setVendors] = useState([]);
  const [payAccounts, setPayAccounts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [vendorId, setVendorId] = useState("");
  const [openBills, setOpenBills] = useState([]);
  const [checked, setChecked] = useState({});
  const [total, setTotal] = useState("");
  const [payFromCode, setPayFromCode] = useState("1050");
  const [method, setMethod] = useState("bank_transfer");
  const [dateStr, setDateStr] = useState(() => new Date().toISOString().slice(0, 10));
  const [memo, setMemo] = useState("");
  const [saving, setSaving] = useState(false);
  const [busyBatch, setBusyBatch] = useState(null);

  const reverseBatch = async (batchId) => {
    if (!window.confirm("Reverse this payment? Bills will re-open and a mirror JE will be posted.")) return;
    setBusyBatch(batchId);
    try {
      const { data } = await api.post(`/payments/batches/${batchId}/reverse`);
      toast.success(`Reversed · ${data.reopened.length} bill${data.reopened.length === 1 ? "" : "s"} re-opened`);
      loadStatic();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reverse failed");
    } finally {
      setBusyBatch(null);
    }
  };

  const loadStatic = useCallback(async () => {
    try {
      const [v, d, b] = await Promise.all([
        api.get("/vendors"),
        api.get("/payments/deposit-accounts"),
        api.get("/payments/batches?kind=vendor_payment&limit=5"),
      ]);
      setVendors(v.data);
      setPayAccounts(d.data);
      setBatches(b.data);
      const def = d.data.find((a) => a.is_default);
      if (def) setPayFromCode(def.code);
    } catch {
      toast.error("Failed to load initial data");
    }
  }, []);
  useEffect(() => { loadStatic(); }, [loadStatic]);

  const loadOpen = useCallback(async () => {
    if (!vendorId) { setOpenBills([]); return; }
    try {
      const { data } = await api.get(`/payments/vendor/open?vendor_id=${vendorId}&currency=${currency}`);
      setOpenBills(data);
      const preset = {};
      data.forEach((b) => { preset[b.id] = true; });
      setChecked(preset);
    } catch {
      toast.error("Failed to load open bills");
    }
  }, [vendorId, currency]);
  useEffect(() => { loadOpen(); }, [loadOpen]);

  const selected = useMemo(() => openBills.filter((b) => checked[b.id]), [openBills, checked]);
  const selectedTotal = useMemo(() => selected.reduce((s, b) => s + b.outstanding, 0), [selected]);
  const applyFullAmount = () => setTotal(selectedTotal.toFixed(2));
  const toggleAll = () => {
    if (openBills.every((b) => checked[b.id])) setChecked({});
    else { const p = {}; openBills.forEach((b) => { p[b.id] = true; }); setChecked(p); }
  };

  const submit = async () => {
    const amt = parseFloat(total);
    if (!vendorId || !amt || amt <= 0) return;
    if (!selected.length) { toast.error("Select at least one bill"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/payments/vendor", {
        vendor_id: vendorId,
        total_amount: amt,
        date: dateStr,
        currency,
        pay_from_account_code: payFromCode,
        method,
        memo,
        allocations: (() => {
          const ordered = [...selected].sort((a, b) => a.date.localeCompare(b.date));
          let remaining = amt;
          const out = [];
          for (const bill of ordered) {
            if (remaining <= 0.005) break;
            const take = Math.min(remaining, bill.outstanding);
            out.push({ bill_id: bill.id, amount: parseFloat(take.toFixed(2)) });
            remaining -= take;
          }
          if (remaining > 0.005 && out.length) {
            out[out.length - 1].amount = parseFloat((out[out.length - 1].amount + remaining).toFixed(2));
          }
          return out;
        })(),
      });
      toast.success(`Paid ${formatCurrency(amt, currency)} · ${data.applied.length} bill${data.applied.length === 1 ? "" : "s"} settled`);
      setTotal(""); setMemo("");
      loadOpen();
      loadStatic();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment failed");
    } finally {
      setSaving(false);
    }
  };

  const currencySym = currency === "NGN" ? "₦" : "$";

  return (
    <AppShell>
      <div className="space-y-6" data-testid="pay-bills-page">
        <header className="flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Accounts Payable</div>
            <h1 className="font-display text-3xl">Pay bills</h1>
            <p className="text-sm text-[var(--muted-text)] mt-1">
              Log a payment to a vendor and auto-apply it across their open bills.
              Posts a balanced journal entry so cash and AP both move.
            </p>
          </div>
          <div className="w-11 h-11 rounded-lg bg-[#FBEEE7] text-[#B4283A] flex items-center justify-center">
            <Wallet className="w-5 h-5" />
          </div>
        </header>

        <div className="card-flat p-5">
          <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)] mb-3">Step 1 · Who you paid</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Vendor</Label>
              <Select value={vendorId} onValueChange={setVendorId}>
                <SelectTrigger data-testid="pb-vendor" className="mt-1"><SelectValue placeholder="Choose vendor" /></SelectTrigger>
                <SelectContent>
                  {vendors.map((v) => (<SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="pb-currency" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD · $</SelectItem>
                  <SelectItem value="NGN">NGN · ₦</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date sent</Label>
              <Input data-testid="pb-date" type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="pb-method" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {vendorId && (
          <div className="card-flat p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Step 2 · Apply to bills</div>
                <p className="text-sm text-[var(--muted-text)] mt-1">
                  {openBills.length ? `${openBills.length} open bill${openBills.length === 1 ? "" : "s"} · ${currency}` : `No open ${currency} bills for this vendor`}
                </p>
              </div>
              {openBills.length > 0 && (
                <Button variant="ghost" size="sm" onClick={toggleAll} data-testid="pb-toggle-all">
                  {openBills.every((b) => checked[b.id]) ? "Uncheck all" : "Check all"}
                </Button>
              )}
            </div>
            {openBills.length > 0 ? (
              <div className="rounded-md border border-[var(--divider)] overflow-hidden">
                <div className="grid grid-cols-12 px-3 py-2 text-[10px] uppercase tracking-wider bg-[var(--hover)] text-[var(--muted-text)]">
                  <div className="col-span-1"></div>
                  <div className="col-span-3">Number</div>
                  <div className="col-span-2">Due</div>
                  <div className="col-span-3">Status</div>
                  <div className="col-span-3 text-right">Outstanding</div>
                </div>
                {openBills.map((bill) => (
                  <div key={bill.id} className="grid grid-cols-12 px-3 py-2 text-sm border-t border-[var(--divider)] items-center hover:bg-[var(--row-hover)]">
                    <div className="col-span-1">
                      <button
                        data-testid={`pb-check-${bill.id}`}
                        onClick={() => setChecked({ ...checked, [bill.id]: !checked[bill.id] })}
                        className="text-[var(--brand)]"
                      >
                        {checked[bill.id] ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                      </button>
                    </div>
                    <div className="col-span-3 font-mono-data">{bill.number}</div>
                    <div className="col-span-2 text-xs text-[var(--muted-text)]">{bill.due_date?.slice(0, 10)}</div>
                    <div className="col-span-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${bill.status === "overdue" ? "bg-[#FBEEE7] text-[#B4283A]" : "bg-[#F5F1E9] text-[#7A6C4E]"}`}>{bill.status}</span>
                    </div>
                    <div className="col-span-3 text-right font-mono-data">{formatCurrency(bill.outstanding, bill.currency)}</div>
                  </div>
                ))}
                <div className="grid grid-cols-12 px-3 py-2 text-sm border-t border-[var(--divider)] bg-[#FBF9F3]/60 font-medium">
                  <div className="col-span-9">Selected outstanding</div>
                  <div className="col-span-3 text-right font-mono-data">{formatCurrency(selectedTotal, currency)}</div>
                </div>
              </div>
            ) : (
              <div className="text-xs text-[var(--muted-text)] py-4">This vendor has no open {currency} bills.</div>
            )}
          </div>
        )}

        {vendorId && openBills.length > 0 && (
          <div className="card-flat p-5">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)] mb-3">Step 3 · Where the money comes from</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Pay from</Label>
                <Select value={payFromCode} onValueChange={setPayFromCode}>
                  <SelectTrigger data-testid="pb-pay-from" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {payAccounts.map((a) => (
                      <SelectItem key={a.code} value={a.code}>
                        {a.code} · {a.name}{a.is_default ? " (default)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount sent</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-text)]">{currencySym}</span>
                  <Input
                    data-testid="pb-amount" type="number" step="0.01" min="0"
                    value={total} onChange={(e) => setTotal(e.target.value)}
                    className="pl-7" placeholder="0.00"
                  />
                </div>
              </div>
              <div className="flex items-end">
                <Button variant="outline" onClick={applyFullAmount} data-testid="pb-fill-total" className="gap-1.5">
                  <Zap className="w-3.5 h-3.5" /> Use selected total ({formatCurrency(selectedTotal, currency)})
                </Button>
              </div>
            </div>
            <div className="mt-4">
              <Label>Memo (optional)</Label>
              <Textarea
                data-testid="pb-memo" value={memo} onChange={(e) => setMemo(e.target.value)}
                placeholder="Check #, wire ref, invoice numbers…"
                rows={2} className="mt-1"
              />
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <Button
                data-testid="pb-submit"
                onClick={submit}
                disabled={saving || !total || parseFloat(total) <= 0 || !selected.length}
                className="btn-brand gap-1.5"
              >
                <DollarSign className="w-4 h-4" /> {saving ? "Posting…" : "Record payment"}
              </Button>
            </div>
          </div>
        )}

        {batches.length > 0 && (
          <div className="card-flat p-5">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)] mb-3">Recent payments</div>
            <div className="rounded-md border border-[var(--divider)] overflow-hidden">
              <div className="grid grid-cols-12 px-3 py-2 text-[10px] uppercase tracking-wider bg-[var(--hover)] text-[var(--muted-text)]">
                <div className="col-span-2">Date</div>
                <div className="col-span-3">Vendor</div>
                <div className="col-span-2">Pay-from</div>
                <div className="col-span-2">Applied to</div>
                <div className="col-span-2 text-right">Amount</div>
                <div className="col-span-1"></div>
              </div>
              {batches.map((b) => {
                const isReversed = b.status === "reversed";
                return (
                  <div key={b.id} data-testid={`pb-batch-${b.id}`} className={`grid grid-cols-12 px-3 py-2 text-sm border-t border-[var(--divider)] ${isReversed ? "opacity-50 line-through" : ""}`}>
                    <div className="col-span-2 font-mono-data">{b.date}</div>
                    <div className="col-span-3">{b.party_name || "Vendor"}</div>
                    <div className="col-span-2 text-xs">
                      {b.pay_from_account_code} · {b.pay_from_account_name}
                      <div className="text-[10px] text-[var(--muted-text)] mt-0.5">{b.method}</div>
                    </div>
                    <div className="col-span-2 text-xs text-[var(--muted-text)]">
                      {(b.applied || []).slice(0, 2).map((a) => a.number).join(", ")}
                      {(b.applied || []).length > 2 ? ` +${b.applied.length - 2}` : ""}
                    </div>
                    <div className="col-span-2 text-right font-mono-data">{formatCurrency(b.total, b.currency)}</div>
                    <div className="col-span-1 flex justify-end">
                      {!isReversed && (
                        <Button data-testid={`pb-reverse-${b.batch_id}`} size="icon" variant="ghost"
                          title="Reverse this payment"
                          disabled={busyBatch === b.batch_id}
                          onClick={() => reverseBatch(b.batch_id)}>
                          <Undo2 className="w-4 h-4 text-[#B4283A]" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
